import { useMemo, useState } from "react";
import { Download, ImageIcon, Paperclip } from "lucide-react";
import { formatBytes } from "../api";
import { AnhangVorschau, anhangUrl, darstellungVon, type Anhang } from "./AnhangVorschau";

/** Grobe Gruppe für die Einfärbung der Dateimarke. */
function typKlasse(ext: string): string {
  if (["PDF"].includes(ext)) return "pdf";
  if (["JPG", "JPEG", "PNG", "GIF", "WEBP", "HEIC", "SVG"].includes(ext)) return "bild";
  if (["DOC", "DOCX", "ODT", "RTF", "TXT"].includes(ext)) return "text";
  if (["XLS", "XLSX", "ODS", "CSV"].includes(ext)) return "tabelle";
  if (["ZIP", "RAR", "7Z", "TAR", "GZ"].includes(ext)) return "archiv";
  if (["ICS"].includes(ext)) return "termin";
  return "sonst";
}

/** Kurzkennung für die Anhangs-Kachel. */
function extensionOf(filename: string): string {
  const ext = filename.includes(".") ? filename.split(".").pop() : undefined;
  return ext && ext.length <= 4 ? ext.toUpperCase() : "DATEI";
}

/**
 * Anhänge als Kacheln. Ein Klick zeigt die Datei an — Bilder und Text in der
 * Vorschau, PDFs im Betrachter des Browsers. Herunterladen ist der kleine
 * Knopf daneben. Signaturbilder und Logos (per cid: im Text eingebettet)
 * stehen schon in der Mail und sind hier nur auf Wunsch zu sehen.
 */
export function Anhaenge({ messageId, attachments }: {
  messageId: number;
  attachments: { filename: string; contentType: string; size: number; inline?: boolean }[];
}) {
  const [vorschau, setVorschau] = useState<number | null>(null);
  const [eingebetteteZeigen, setEingebetteteZeigen] = useState(false);

  // Der Index bleibt der in der Mail — der Server lädt darüber.
  const { echte, eingebettete } = useMemo(() => {
    const alle: (Anhang & { inline: boolean })[] = attachments.map((a, index) => ({ ...a, index, inline: Boolean(a.inline) }));
    return { echte: alle.filter((a) => !a.inline), eingebettete: alle.filter((a) => a.inline) };
  }, [attachments]);

  const sichtbar = eingebetteteZeigen ? [...echte, ...eingebettete] : echte;
  if (sichtbar.length === 0 && eingebettete.length === 0) return null;

  const oeffnen = (a: Anhang, i: number) => {
    const art = darstellungVon(a);
    if (art === "pdf") window.open(anhangUrl(messageId, a, true), "_blank", "noopener");
    else setVorschau(i);
  };

  return (
    <section className="anhaenge" aria-label="Anhänge">
      {echte.length > 0 && (
        <div className="anhaenge-kopf">
          <Paperclip aria-hidden="true" />
          <b>{echte.length} {echte.length === 1 ? "Anhang" : "Anhänge"}</b>
          <span>{formatBytes(echte.reduce((s, a) => s + a.size, 0))}</span>
        </div>
      )}

      {sichtbar.length > 0 && (
        <ul className="anhang-kacheln">
          {sichtbar.map((a, i) => {
            const ext = extensionOf(a.filename);
            const art = darstellungVon(a);
            return (
              <li key={a.index} className="anhang-kachel" data-eingebettet={a.inline || undefined}>
                <button
                  type="button"
                  className="anhang-oeffnen"
                  onClick={() => oeffnen(a, i)}
                  title={art === "keine" ? `${a.filename} — keine Vorschau, öffnet den Download` : `${a.filename} ansehen`}
                >
                  <span className="datei-marke" data-typ={typKlasse(ext)}>{ext}</span>
                  <span className="anhang-text">
                    <span className="anhang-name">{a.filename || "(ohne Namen)"}</span>
                    <span className="anhang-groesse">{formatBytes(a.size)}{art === "keine" ? "" : " · ansehen"}</span>
                  </span>
                </button>
                <a
                  className="icon-btn anhang-laden"
                  href={anhangUrl(messageId, a)}
                  download={a.filename}
                  title={`${a.filename} herunterladen`}
                  aria-label={`${a.filename} herunterladen`}
                >
                  <Download aria-hidden="true" />
                </a>
              </li>
            );
          })}
        </ul>
      )}

      {eingebettete.length > 0 && (
        <button type="button" className="link-knopf anhaenge-eingebettet" onClick={() => setEingebetteteZeigen((v) => !v)}>
          <ImageIcon aria-hidden="true" />
          {eingebetteteZeigen
            ? "Eingebettete Bilder ausblenden"
            : `${eingebettete.length} eingebettete${eingebettete.length === 1 ? "s Bild" : " Bilder"} (Signatur, Logos) anzeigen`}
        </button>
      )}

      {vorschau !== null && sichtbar[vorschau] && (
        <AnhangVorschau messageId={messageId} anhaenge={sichtbar} start={vorschau} onClose={() => setVorschau(null)} />
      )}
    </section>
  );
}
